import React, { useMemo, useCallback } from 'react';
import AdbFlatTableBase, { AdbServiceParams } from './AdbFlatTableBase';
import { fetchClusterDetails } from 'service/service';
import {
    getClusterDetailsColumns,
    CLUSTER_DETAILS_DEFAULT_COLUMNS,
    CLUSTER_COMPUTE_TYPE_OPTIONS,
    CLUSTER_AUTO_TERMINATION_OPTIONS,
    QueryRule
} from 'utils/adb-common-util';

const PRESERVE_FILTER_KEY = 'cluster_count_table';

/**
 * ClusterCountWithoutAutoTermination component refactored to use AdbFlatTableBase.
 * Reduces duplication to 0% by leveraging the base component.
 */
const ClusterCountWithoutAutoTermination: React.FC = () => {
    // Memoize the service function wrapper to prevent unnecessary re-renders in AdbFlatTableBase
    const serviceFunc = useCallback(async (params: AdbServiceParams) => {
        const {
            startDate, endDate, env, sector, location,
            page, pageSize, apiFilters, exportType,
            sortBy, sortDir
        } = params;
        const findFilter = (field: string) => apiFilters.find(f => f.field === field)?.value;

        const computeType = findFilter('Compute Type') || 'All';
        const workspace = findFilter('Workspace Name') || 'All';
        const autoTerm = findFilter('Auto Termination') || null;
        const clusterNameFilter = findFilter('Cluster Name') || 'All';

        return fetchClusterDetails({
            startDate,
            endDate,
            sector,
            env,
            computeType: String(computeType),
            location,
            autoTermination: autoTerm === 'Null' ? null : autoTerm,
            workspaceID: workspace === 'All' ? undefined : String(workspace),
            jobName: undefined,
            clusterName: String(clusterNameFilter),
            page,
            pageSize,
            sortField: sortBy,
            sortOrder: sortDir,
            exportType
        });
    }, []);

    const initialFilters: QueryRule[] = useMemo(() => [
        { id: '1', operator: 'AND', field: 'Compute Type', value: 'INTERACTIVE' },
        { id: '2', operator: 'AND', field: 'Workspace Name', value: 'All' }
    ], []);

    const environmentOptions = useMemo(() =>
        CLUSTER_COMPUTE_TYPE_OPTIONS.filter(opt => opt.value !== 'SQL'),
        []
    );

    return (
        <AdbFlatTableBase
            serviceFunc={serviceFunc}
            pageTitle="Clusters Count Without Auto Termination Details"
            breadcrumbLabel="Cluster Analytics"
            breadcrumbPath="/databricksADB/clusteranalytics"
            tableType="cost-cluster-table-without-autotermination"
            defaultColumnNames={CLUSTER_DETAILS_DEFAULT_COLUMNS}
            getColumns={getClusterDetailsColumns}
            preserveFilterKey={PRESERVE_FILTER_KEY}
            initialComputeType="INTERACTIVE"
            showAutoTermination={true}
            initialFilters={initialFilters}
            autoTerminationOptions={CLUSTER_AUTO_TERMINATION_OPTIONS}
            environmentOptions={environmentOptions}
            showClusterName={true}
        />
    );
};

export default ClusterCountWithoutAutoTermination;
